// Runs (design/isomorphic runs): status filter chips over a table of the current
// organisation's runs, newest first. The filter is in the URL (?status=waiting).
import { ListChecks } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import type { Environment, Run, Workflow, Workspace } from "../api/client";
import { environmentsQuery, runsQuery, workflowsQuery, workspacesQuery } from "../api/queries";
import { type Messages, useT } from "../i18n";
import { useCurrentOrg } from "../layout/currentOrg";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { type Column, DataTable } from "../ui/DataTable";
import { FilterChips } from "../ui/FilterChips";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Mono, StatusPill } from "../ui/status";
import { runTone } from "../ui/tones";
import { dayTime, elapsed, kTokens, usd } from "../workloads/format";
import { TriggerIcon } from "../workloads/TriggerIcon";
import { filterCounts, matchesFilter, newestFirst, parseFilter, RUN_FILTERS, type RunFilter } from "../workloads/runs";
import { sectionCrumbs } from "../layout/nav";

interface Lookup {
  workflow: (r: Run) => Workflow | undefined;
  where: (r: Run) => string;
}

function columns(t: Messages["workloads"]["runs"], { workflow, where }: Lookup): Column<Run>[] {
  const c = t.columns;
  return [
    {
      key: "run",
      header: c.run,
      cell: (r) => {
        return (
          <Link to={`/runs/${r.id}`} className="group flex items-center gap-3" title={r.id}>
            <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 sm:flex">
              <TriggerIcon trigger={r.trigger_detail} className="h-[18px] w-[18px]" />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-semibold text-gray-900 group-hover:text-primary">{workflow(r)?.name ?? t.fallbackName}</span>
              <Mono className="text-[11.5px] text-gray-500">{r.id.slice(0, 12)}…</Mono>
            </span>
          </Link>
        );
      },
    },
    { key: "status", header: c.status, cell: (r) => <StatusPill tone={runTone(r.status)} status={r.status} /> },
    { key: "where", header: c.where, cell: (r) => <span className="text-gray-600">{where(r)}</span>, hideBelow: "xl" },
    { key: "queued", header: c.queued, cell: (r) => <span className="whitespace-nowrap">{dayTime(r.queued_at)}</span>, hideBelow: "md" },
    { key: "duration", header: c.duration, cell: (r) => <span className="tabular">{elapsed(r.started_at, r.finished_at)}</span>, hideBelow: "lg" },
    {
      key: "tokens",
      header: c.tokens,
      cell: (r) => (
        <span className="tabular whitespace-nowrap text-gray-600">
          {r.input_tokens == null ? "—" : `${kTokens(r.input_tokens)} / ${kTokens(r.output_tokens ?? 0)}`}
        </span>
      ),
      hideBelow: "lg",
    },
    { key: "cost", header: c.cost, cell: (r) => <span className="tabular font-semibold text-gray-900">{usd(r.cost_usd)}</span>, className: "text-end" },
  ];
}

function lookup(workflows: Workflow[], environments: Environment[], workspaces: Workspace[]): Lookup {
  const workflow = (r: Run) => workflows.find((w) => w.id === r.workflow_id);
  return {
    workflow,
    where: (r) => {
      const w = workflow(r);
      const env = environments.find((e) => e.id === w?.environment_id)?.name;
      const ws = workspaces.find((x) => x.id === w?.workspace_id)?.name;
      return [env, ws].filter(Boolean).join(" · ") || "—";
    },
  };
}

export default function RunsPage() {
  const { workloads } = useT();
  const t = workloads.runs;
  const [params, setParams] = useSearchParams();
  const filter = parseFilter(params.get("status"));
  const { org } = useCurrentOrg();
  const runs = useQuery(runsQuery());
  const workflows = useQuery(workflowsQuery());
  const environments = useQuery(environmentsQuery());
  const workspaces = useQuery(workspacesQuery());
  const mine = newestFirst((runs.data ?? []).filter((r) => org === undefined || r.org_id === org.id));
  const rows = mine.filter((r) => matchesFilter(r, filter));
  const cols = columns(t, lookup(workflows.data ?? [], environments.data ?? [], workspaces.data ?? []));

  return (
    <>
      <PageHeader
        title={t.title}
        breadcrumb={sectionCrumbs("runs")}
        description={t.description}
      />
      <FilterChips
        className="mb-4"
        label={t.filterLabel}
        chips={RUN_FILTERS.map((key) => ({ key, label: t.filters[key] }))}
        value={filter}
        counts={filterCounts(mine)}
        onChange={(f: RunFilter) => setParams(f === "all" ? {} : { status: f }, { replace: true })}
      />
      {runs.isPending ? <Loading /> : null}
      {runs.isError ? <ErrorState message={runs.error.message} /> : null}
      {runs.isSuccess && rows.length === 0 ? (
        <EmptyState icon={<ListChecks weight="duotone" />} title={mine.length === 0 ? t.emptyNone : t.emptyFiltered}>
          {t.emptyBody}
        </EmptyState>
      ) : null}
      {rows.length > 0 ? (
        <div className={cn(cardClass, "p-3 lg:p-4")}>
          <DataTable label={t.title} columns={cols} rows={rows} rowKey={(r) => r.id} />
        </div>
      ) : null}
      <p className="mt-3 text-xs text-gray-500">{t.costNote}</p>
    </>
  );
}
